import { GlobalAuthContext, GlobalAuthContextType, AuthContext } from '@@/contexts/AuthContext';
import React from 'react';
import { Outlet } from 'react-router-dom';
import { useGetUserRolePermissions } from '@@/hooks/useGetUserRolePermissions';
import { getRolesPermissionsResponse } from '@@/api/api-common';

// Factory function to create global instance data
const createGlobalInstanceData = (permissions?: getRolesPermissionsResponse) => {
  if (!permissions) return undefined;

  return {
    instanceId: 'global',
    instanceAlias: 'Global',
    companyId: '',
    companyName: '',
    awsAccountId: '',
    region: '',
    assistantId: '',
    instanceArn: '',
    domainId: '',
    status: 'active',
    permissions: permissions.globalPermissions || []
  };
};

// Factory function to create global auth context value
const createGlobalAuthContextValue = (
  permissions?: getRolesPermissionsResponse,
  isPending = false,
  isRefetching = false,
  contextError: Error | null = null,
  error: Error | null = null,
  refetch: () => Promise<unknown> = () => Promise.resolve(),
  user = { id: '', email: '' }
): GlobalAuthContextType => {
  const globalInstanceData = createGlobalInstanceData(permissions);

  // Static callback functions for global context
  const setCurrentInstance = () => {}; // No-op for global context
  const getDropdownInstanceOptions = () => []; // No instances in global context
  const getTableInstanceData = () => []; // No instances in global context
  const getPathWithInstanceAlias = (path: string) => path; // No instance alias needed

  return {
    currentInstance: 'global',
    setCurrentInstance,
    instanceData: globalInstanceData,
    loading: isPending || isRefetching,
    error: contextError || error,
    getDropdownInstanceOptions,
    getTableInstanceData,
    refetch,
    getPathWithInstanceAlias,
    user,
    globalPermissions: permissions?.globalPermissions || []
  };
};

interface GlobalAuthProviderProps {
  children?: React.ReactNode;
}

const GlobalAuthProvider: React.FC<GlobalAuthProviderProps> = ({ children }) => {
  const { permissions, isPending, error, refetch, isRefetching, user } = useGetUserRolePermissions();

  const value = createGlobalAuthContextValue(permissions, isPending, isRefetching, null, error, refetch, user);

  return (
    <GlobalAuthContext.Provider value={value}>
      <AuthContext.Provider value={value}>{children || <Outlet />}</AuthContext.Provider>
    </GlobalAuthContext.Provider>
  );
};

export default GlobalAuthProvider;
